<template>
  <div class="blog">
    <b-loading :is-full-page="false" :active.sync="loading" :can-cancel="false" />
    <slot name="category" />
    <div class="content">
      <page-title>
        {{ $t('fields.blog') }}
      </page-title>
    </div>

    <div class="content blog-container">
      <div v-for="(post, index) in data" :key="index" class="blog-item">
        <div class="blog-item-image">
          <nuxt-link :to="localePath({name: 'cms-postPage', params: {slug: post.url}})">
            <img v-if="post.mainImage && post.mainImage.sizes" :src="post.mainImage.sizes.huge" :alt="post.description.name" loading="lazy">
            <img v-else src="../../static/photo.jpg" alt="">
          </nuxt-link>
        </div>
        <div class="blog-item-details">
          <div class="blog-item-header">
            <avatar :name="post.creator" />
            <time :datetime="$moment(post.dateOfPublication).format('YYYY-MM-DD HH:MM')">{{ $moment(post.dateOfPublication).format('L') }}</time>
          </div>
          <nuxt-link :to="localePath({name: 'cms-postPage', params: {slug: post.url}})">
            <h2 class="blog-item-title">
              {{ post.description.name }}
            </h2>
          </nuxt-link>
        </div>
      </div>
    </div>

    <!-- <b-pagination
      v-if="total > 0"
      class="m-t-20"
      :total="total"
      :current="page"
      :per-page="limit"
      :range-before="3"
      :range-after="3"
      @change="onPageChange"
    /> -->

    <div class="content">
      <nav v-if="total > 0" class="pagination m-t-20">
        <a
          role="button"
          :href="pagination.hasPrev ? getLink(page - 1) : '#'"
          class="pagination-link pagination-previous"
          :disabled="!pagination.hasPrev"
          @click.prevent="pagination.hasPrev ? onPageChange(page - 1) : null"
        >
          <span class="icon" aria-hidden="true">
            <i class="core-icons core-icons-chevron-left is-size-5" />
          </span>
        </a>

        <a
          role="button"
          :href="pagination.hasNext ? getLink(page + 1) : '#'"
          class="pagination-link pagination-next"
          :disabled="!pagination.hasNext"
          @click.prevent="pagination.hasNext ? onPageChange(page + 1) : null"
        >
          <span class="icon" aria-hidden="true">
            <i class="core-icons core-icons-chevron-right is-size-5" />
          </span>
        </a>

        <ul class="pagination-list">
          <li v-for="(element, index) in pagination.pages" :key="index">
            <template v-if="element.isEllipsis">
              <span class="pagination-ellipsis">&hellip;</span>
            </template>
            <template v-else>
              <a
                role="button"
                :href="getLink(element.page)"
                class="pagination-link"
                :class="{ 'is-current': element.isCurrent }"
                :aria-current="element.isCurrent"
                @click.prevent="onPageChange(element.page)"
              >
                {{ element.page }}
              </a>
            </template>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import pagination from '~/marketplace-front-core/utils/pagination'
import Avatar from '~/marketplace-front-core/components/shared/Avatar'

export default {
  name: 'PageBlog',
  components: { Avatar },
  layout: 'page',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    page: {
      type: Number,
      default: 1
    },
    total: {
      type: Number,
      default: null
    },
    limit: {
      type: Number,
      default: 10
    },
    countLangs: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      loading: false
    }
  },
  head () {
    const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true })
    // const frontBaseUrl = process.env.frontBaseUrl

    return {
      title: this.$t('fields.blog'),
      htmlAttrs: {
        ...i18nHead.htmlAttrs
      },
      meta: [
        ...i18nHead.meta
      ],
      link: [
        // ...this.metaLink(i18nHead.link)
        ...this.linksSeo(this.metaLink(i18nHead.link))
        // ...this.pagination.pages
        //   .filter(x => !x.isEllipsis && !x.isCurrent)
        //   .map((x) => { return { rel: 'canonical', href: frontBaseUrl + this.getLink(x.page) } })
      ]
    }
  },
  computed: {
    pagination () {
      return pagination(this.page, this.total, this.limit)
    }
  },
  watch: {
    '$route.query.page' (newPage) {
      newPage = parseInt(newPage) || 1
      if (this.page !== newPage) {
        this.onPageChange(newPage)
      }
    },
    currentLang (lang) {
      this.onPageChange(1)
    }
  },
  methods: {
    loadAsyncData (page) {
      this.loading = true
      this.$api.cms.list({
        page,
        limit: this.limit,
        lang: this.currentLang
      })
        .then((res) => {
          this.$emit('update:data', res.query)
          this.$emit('update:total', res.count)
          this.$emit('update:countLangs', res.countLangs)
        })
        .catch(() => {
          this.$emit('update:data', [])
          this.$emit('update:total', 0)
          this.$emit('update:countLangs', null)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getLink (page) {
      const query = this._clone(this.$route.query)
      if (page === 1) { delete query.page } else { query.page = page }
      return this.localePath({ name: 'cms-blog', query })
    },
    onPageChange (page) {
      const query = this._clone(this.$route.query)
      if (page === 1) { delete query.page } else { query.page = page }
      this.$router.push(this.localePath({ name: 'cms-blog', query }))
      this.$emit('update:page', page)
      this.scrollToTop('smooth')
      this.loadAsyncData(page)
    },
    linksSeo (data) {
      const frontBaseUrl = process.env.frontBaseUrl
      let links = this._clone(data)

      const find = links.find(item => item.rel === 'canonical')
      if (find) {
        find.href = frontBaseUrl + this.getLink(this.page)
      }

      links = links.filter((item) => {
        if (item.rel !== 'alternate' || !item.hreflang) {
          return true
        }
        if (!this.countLangs) {
          return false
        }
        const langPrefix = item.hreflang.split('-')[0]
        const count = parseInt(this.countLangs[langPrefix]) || 0
        const totalPages = Math.ceil(count / this.limit)
        return this.page <= totalPages
      })

      return links
    }
  }
}
</script>
